import { useEffect, useRef, useState } from "react";
import { loadTurnstileScript } from "../../lib/paypal";

interface TurnstileFieldProps {
  siteKey: string | null;
  onTokenChange: (token: string | null) => void;
}

export const TurnstileField = ({ siteKey, onTokenChange }: TurnstileFieldProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const callbackRef = useRef(onTokenChange);
  const [error, setError] = useState<string | null>(null);

  callbackRef.current = onTokenChange;

  useEffect(() => {
    let active = true;

    if (!siteKey || !containerRef.current) {
      callbackRef.current(null);
      return;
    }

    loadTurnstileScript()
      .then(() => {
        if (!active || !containerRef.current || !window.turnstile) {
          return;
        }

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme: "light",
          callback: (token: string) => {
            callbackRef.current(token);
            setError(null);
          },
          "expired-callback": () => callbackRef.current(null),
          "error-callback": () => {
            callbackRef.current(null);
            setError("Verification could not be completed. Please try again.");
          },
        });
      })
      .catch((loadError) => {
        callbackRef.current(null);
        setError(loadError instanceof Error ? loadError.message : "Verification could not be loaded.");
      });

    return () => {
      active = false;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  if (!siteKey) {
    return null;
  }

  return (
    <div className="space-y-2">
      <div ref={containerRef} />
      {error ? <p className="text-sm text-red-700">{error}</p> : null}
    </div>
  );
};
